import { useQuery } from '@tanstack/react-query';
import { ordersApi } from '@/lib/api/client';
import type { OpenOrder } from '@/lib/api/client';

export const orderKeys = {
  all: ['orders'] as const,
  open: () => [...orderKeys.all, 'open'] as const,
  bySymbol: (symbol: string) => [...orderKeys.all, 'bySymbol', symbol] as const,
  analysis: (orderId: number) => [...orderKeys.all, 'analysis', orderId] as const,
};

export function useOpenOrders(enabled = true) {
  return useQuery({
    queryKey: orderKeys.open(),
    queryFn: () => ordersApi.getOpen(),
    select: (data) => data.orders,
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 3 * 60 * 1000, // 3 minutes
  });
}

export function useOrdersBySymbol(symbol: string, enabled = true) {
  return useQuery({
    queryKey: orderKeys.bySymbol(symbol),
    queryFn: () => ordersApi.getBySymbol(symbol),
    select: (data) => data.orders,
    enabled: enabled && !!symbol,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
  });
}

export function useOrderAnalysis(order: OpenOrder | null, enabled = true) {
  const underlying = order?.legs.find(l => l.underlyingSymbol)?.underlyingSymbol
    || order?.legs.find(l => l.assetType === 'EQUITY')?.symbol
    || '';
  const hasOptionLegs = order?.legs.some(l => l.assetType === 'OPTION') ?? false;

  return useQuery({
    queryKey: orderKeys.analysis(order?.orderId ?? 0),
    queryFn: () => ordersApi.analyze({
      underlyingSymbol: underlying,
      orderType: order!.orderType,
      limitPrice: order!.price != null ? order!.price * order!.quantity * 100 : null,
      legs: order!.legs.map(l => ({
        instruction: l.instruction,
        putCall: l.putCall,
        strikePrice: l.strikePrice,
        expirationDate: l.expirationDate,
        quantity: l.quantity,
        assetType: l.assetType,
      })),
    }),
    select: (data) => data.analysis,
    enabled: enabled && !!order && hasOptionLegs && !!underlying,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}
